import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 20000;

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(app, pubsubToken, websocketHost = '') {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    // Whether the server confirmed the subscription, as opposed to
    // `connection.isOpen()` which only reports the socket. The two drift apart,
    // and only this one says whether broadcasts can arrive.
    this.isSubscribed = false;
    this.hasSubscribedBefore = false;
    // A super admin "Impersonate user" session must not mark the agent online:
    // it sends no presence and tells the server so on subscribe.
    const isImpersonating = !!app.$store.getters.isImpersonating;
    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
        ...(isImpersonating && { impersonated: true }),
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        connected: this.onConnected,
        disconnected: () => {
          this.isSubscribed = false;
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.isAValidEvent = () => true;
    this.triggerPresenceInterval = () => {
      setTimeout(() => {
        // Performing on a subscription the server does not hold is dropped on
        // the floor and raises server side, so wait for the confirmation.
        if (this.isSubscribed) this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, PRESENCE_INTERVAL);
    };
    if (!isImpersonating) this.triggerPresenceInterval();
  }

  // Fires on the server's confirmation, first subscribe and recovery alike, and
  // is the only signal that broadcasts can actually arrive. ActionCable 6.1
  // passes no payload, so which of the two this is has to be tracked here.
  onConnected = () => {
    const reconnected =
      this.hasSubscribedBefore || BaseActionCableConnector.isDisconnected;
    this.isSubscribed = true;
    this.hasSubscribedBefore = true;
    this.onSubscribed({ reconnected });
    BaseActionCableConnector.isDisconnected = false;
  };

  // Every confirmation lands here, so a consumer that has work to do on a first
  // subscribe as well as a recovery overrides this one. The default keeps the
  // historical contract for those that only care about recovery.
  onSubscribed = ({ reconnected }) => {
    if (reconnected) this.onReconnect();
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
